import React, { memo, useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import Pagination from '@lib/components/Pagination/Pagination';
import TableEmptyState from '@lib/components/Table/components/TableEmptyState/TableEmptyState';
import CircleLoader, { Types } from '@lib/components/CircleLoader/CircleLoader';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import {
  checkHasActionsColumn,
  checkHasActionsMenuColumn,
  checkHasSelectColumn,
} from '@lib/components/Table/utils';
import useEventListener from '@lib/hooks/useEventListener';
import { MIN_TABLE_HEIGHT } from '@lib/components/Table/constants';
import TableHead from './components/TableHead/TableHead';
import TableBody from './components/TableBody/TableBody';
import TableFooter from './components/TableFooter/TableFooter';
import TableCounter from './components/TableCounter/TableCounter';
import { BaseItem, TableProps } from './types';
import styles from './Table.module.scss';

function Table<T extends BaseItem>(props: TableProps<T>) {
  const {
    Footer,
    actions,
    actionsForSelected,
    actionsMenu,
    bordered = true,
    borderedCells,
    className,
    columns,
    components,
    counterText,
    data,
    fullHeight,
    getIsRowDisabled,
    getIsRowSelected,
    getRowClassName,
    id,
    loading,
    onRowClick,
    onSortChange,
    orderingDirectionKey,
    orderingFieldKey,
    orderingState,
    paginationState,
    rounded = true,
    selectionLimit,
    tableContainerClassName,
    totalEntries = 0,
    useBulkSelectResult = {} as UseBulkSelectResult<T>,
  } = props;
  const {
    unselectedTableItems = [],
    selectedTableItems = [],
    isAllTableItemsSelected = false,
  } = useBulkSelectResult;
  const componentRef = useRef<HTMLDivElement>(null);
  const [componentHeight, setComponentHeight] = useState<number | null>(null);

  const isTableVisible = !!totalEntries && !!data.length;
  const isEmptyStateVisible = !isTableVisible;
  const isPaginationVisible = isTableVisible && totalEntries > data.length;
  const isCounterVisible = !!counterText && !!totalEntries;
  const { TopHeadComponent } = components || {};
  const isTopHeadComponentVisible =
    isTableVisible && TopHeadComponent !== undefined;

  const hasActionsColumn = checkHasActionsColumn(props);
  const hasActionsMenuColumn = checkHasActionsMenuColumn(props);
  const hasSelectColumn = checkHasSelectColumn(props);

  const isSelectAllCheckboxIndeterminate =
    hasSelectColumn &&
    ((isAllTableItemsSelected &&
      unselectedTableItems.length !== 0 &&
      unselectedTableItems.length < totalEntries) ||
      (selectedTableItems.length > 0 &&
        selectedTableItems.length < totalEntries));
  const isSelectAllCheckboxChecked =
    (isAllTableItemsSelected && unselectedTableItems.length < totalEntries) ||
    selectedTableItems.length > 0;

  const isSelectionLimitReached =
    selectionLimit != null && selectedTableItems.length >= selectionLimit;
  const isSelectAllDisabledByLimit =
    selectionLimit != null &&
    (totalEntries > selectionLimit || isSelectionLimitReached);

  const updateComponentHeight = useCallback(() => {
    if (fullHeight && componentRef.current) {
      const windowHeight = window.innerHeight;
      const rect = componentRef.current.getBoundingClientRect();

      // Sum paddingBottom and borderBottom from all parent elements
      let totalBottomOffset = 0;
      let parent = componentRef.current.parentElement;
      while (parent) {
        const parentStyle = window.getComputedStyle(parent);
        totalBottomOffset += parseInt(parentStyle.paddingBottom, 10) || 0;
        totalBottomOffset += parseInt(parentStyle.borderBottomWidth, 10) || 0;
        parent = parent.parentElement;
      }

      // Available height = viewport height - table's top position - all parents' bottom offset
      const newComponentHeight = windowHeight - rect.top - totalBottomOffset;

      setComponentHeight(
        newComponentHeight > MIN_TABLE_HEIGHT
          ? newComponentHeight
          : MIN_TABLE_HEIGHT,
      );
    }
  }, [fullHeight]);

  useEventListener('resize', updateComponentHeight);

  return (
    <div
      className={classNames(
        styles.root,
        {
          [styles.rootWithCounter]: isCounterVisible,
          [styles.rootWithScroll]: !!componentHeight,
          [styles.rootWithLoading]: loading,
        },
        className,
      )}
      data-test-id={id ? `table__${id}` : undefined}
      ref={componentRef}
      style={{ maxHeight: componentHeight ? `${componentHeight}px` : 'auto' }}
    >
      {loading && (
        <span className={styles.tableLoader}>
          <CircleLoader size={48} strokeWidth={4} type={Types.GIF} />
        </span>
      )}
      {isCounterVisible && (
        <TableCounter
          totalEntries={totalEntries}
          counterText={counterText}
          className={styles.tableCounter}
        />
      )}
      {isTopHeadComponentVisible && (
        <div className={styles.topHead}>
          <TopHeadComponent />
        </div>
      )}
      {isEmptyStateVisible ? (
        <TableEmptyState {...props} />
      ) : (
        <>
          {!!actionsForSelected?.length && isSelectAllCheckboxChecked && (
            <div
              className={classNames(styles.actionsRow, {
                [styles.actionsRowWhenCounter]: isCounterVisible,
              })}
              data-test-id="table__bulk-actions"
            >
              {actionsForSelected.map((item) => item)}
            </div>
          )}
          <div
            className={classNames(
              styles.tableContainer,
              {
                [styles.tableContainerWithTopHead]: isTopHeadComponentVisible,
                [styles.tableContainerWithPagination]: isPaginationVisible,
                [styles.tableContainerWithLoading]: loading,
                [styles.tableContainerWithRounded]: rounded,
                [styles.tableContainerWithBordered]: bordered,
                [styles.tableContainerWithScroll]: fullHeight,
              },
              tableContainerClassName,
            )}
          >
            <table className={styles.table}>
              <TableHead<T>
                borderedCells={borderedCells}
                columns={columns}
                data={data}
                hasActionsColumn={hasActionsColumn}
                hasActionsMenuColumn={hasActionsMenuColumn}
                hasSelectColumn={hasSelectColumn}
                isSelectAllCheckboxChecked={isSelectAllCheckboxChecked}
                isSelectAllCheckboxIndeterminate={
                  isSelectAllCheckboxIndeterminate
                }
                isSelectAllDisabledByLimit={isSelectAllDisabledByLimit}
                selectionLimit={selectionLimit}
                onSortChange={onSortChange}
                orderingDirectionKey={orderingDirectionKey}
                orderingFieldKey={orderingFieldKey}
                orderingState={orderingState}
                useBulkSelectResult={useBulkSelectResult}
              />
              <TableBody<T>
                actions={actions}
                actionsMenu={actionsMenu}
                columns={columns}
                data={data}
                getIsRowDisabled={getIsRowDisabled}
                getIsRowSelected={getIsRowSelected}
                getRowClassName={getRowClassName}
                hasActionsColumn={hasActionsColumn}
                hasActionsMenuColumn={hasActionsMenuColumn}
                hasSelectColumn={hasSelectColumn}
                isSelectionLimitReached={isSelectionLimitReached}
                onRowClick={onRowClick}
                selectionLimit={selectionLimit}
                useBulkSelectResult={useBulkSelectResult}
              />
              <TableFooter
                Footer={Footer}
                columns={columns}
                data={data}
                hasActionsColumn={hasActionsColumn}
              />
            </table>
          </div>
        </>
      )}
      {isPaginationVisible && (
        <div
          className={classNames(styles.pagination, {
            [styles.paginationWithRounded]: rounded,
            [styles.paginationWithBordered]: bordered,
          })}
          data-test-id="table__pagination"
        >
          <Pagination {...props} paginationState={paginationState} />
        </div>
      )}
    </div>
  );
}

export default memo(Table) as <T extends BaseItem>(
  props: TableProps<T>,
) => React.ReactElement;
